import styles from "./Modals.module.scss";
import React, { useCallback, useRef, useState } from "react";
import type { Destination, IncidentTypeValue } from "@whatslly/types/messages-server/destinations";
import { DESTINATION_INTEGRATION_TYPES } from "@whatslly/types/messages-server/destinations";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { FormikErrors, FormikProps } from "formik";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { useTranslation } from "react-i18next";
import { useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { DestinationsStore } from "../DestinationsStore";
import { DestinationsModalButtons } from "../DestinationsModalButtons";
import { AuditLogNamespaceSelect } from "../components/AuditLogNamespaceSelect";
import omit from "lodash/omit";
import { FormPasswordTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput/FormPasswordTextInput";
import type { DestinationFormProps, FormDestinationDataType } from "../utils/DestinationsListTypes";
import { IncidentTypesSelect } from "../components/IncidentTypesSelect";
import { getUrlOrigin } from "@whatslly/commons/urlUtils";
import { useDestinationTestConnectionSubmit } from "../utils/useDestinationTestConnectionSubmit";

const BASE_DATA: Destination = {
  type: "googleSecOpsAuditLog" as const,
  integrationType: DESTINATION_INTEGRATION_TYPES.AUDIT_LOGS,
  settings: { googApiKey: "", webhookAccessKey: "", serverUrl: "", namespaces: [] },
};

type FormDataType = FormDestinationDataType<"googleSecOpsAuditLog" | "googleSecOpsDlpIncident"> & {
  namespaces?: string[];
  incidentTypes?: IncidentTypeValue[];
};

export const GoogleSecOpsModalForm: React.FC<DestinationFormProps<"googleSecOpsAuditLog" | "googleSecOpsDlpIncident">> = ({
  settings,
  destinationType,
  onSubmitForm,
}) => {
  const { t } = useTranslation();
  const store = useLocalStoreWithInjectedDependencies(DestinationsStore);
  const formikContext = useRef<FormikProps<FormDataType>>(null);
  const [formikErrorHasErrors, setFormikErrorHasErrors] = useState(true);

  const validate = async (values: Omit<FormDataType, "namespaces"> & { namespaces?: string[] }) => {
    const errors: FormikErrors<PartialRecord<keyof FormDataType, string>> = {};
    if (!values.googApiKey) {
      errors.googApiKey = t("field_is_required");
    }
    if (!values.webhookAccessKey) {
      errors.webhookAccessKey = t("field_is_required");
    }
    if (!values.serverUrl) {
      errors.serverUrl = t("field_is_required");
    }
    if (!errors.serverUrl && !getUrlOrigin(values.serverUrl)) {
      errors.serverUrl = t("invalid_url");
    }
    if (destinationType === "googleSecOpsDlpIncident" && !values.incidentTypes?.length) {
      errors.incidentTypes = t("field_is_required");
    }
    setFormikErrorHasErrors(Object.keys(errors).length > 0);
    return errors;
  };

  const getFormValues = useCallback(() => {
    const data = (formikContext.current?.values ?? {}) as FormDataType;
    if (destinationType === "googleSecOpsDlpIncident") {
      return {
        integrationType: DESTINATION_INTEGRATION_TYPES.DLP_INCIDENTS,
        type: destinationType,
        settings: omit(data, ["namespaces"]),
      };
    }
    return {
      integrationType: DESTINATION_INTEGRATION_TYPES.AUDIT_LOGS,
      type: destinationType,
      settings: { ...omit(data, ["incidentTypes"]), namespaces: data.namespaces ?? [] },
    };
  }, [destinationType]);

  const onSubmit = useDestinationTestConnectionSubmit({ getFormValues, validate, onSubmitForm, store, t });

  return (
    <>
      <FormElement initialValues={settings || BASE_DATA.settings} validate={validate} onSubmit={onSubmit} formikRef={formikContext}>
        {destinationType === "googleSecOpsAuditLog" && (
          <div className={styles.field}>
            <Label className={styles.filedLabel} text={t("destinations_page.type")} required />
            <AuditLogNamespaceSelect name="namespaces" />
          </div>
        )}
        {destinationType === "googleSecOpsDlpIncident" && <IncidentTypesSelect name="incidentTypes" />}
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.webhook_access_key")} required />
          <FormPasswordTextInput name="webhookAccessKey" paddingInline="none" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.google_api_key")} required />
          <FormPasswordTextInput name="googApiKey" paddingInline="none" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.server_url")} required />
          <FormTextInput name="serverUrl" paddingInline="none" />
        </div>
      </FormElement>
      <DestinationsModalButtons formikContext={formikContext} formikErrorHasErrors={formikErrorHasErrors} store={store} />
    </>
  );
};
